/**
 * Centralised reads of build-time feature flags so callers don't sprinkle
 * `import.meta.env.VITE_...` checks across the codebase.
 */

function readBoolFlag(name: string, defaultValue = false): boolean {
  const raw = import.meta.env[name]
  if (raw == null || raw === '') return defaultValue
  return String(raw).toLowerCase() === 'true'
}

/**
 * Multi-dimensional A-F match scoring UI (see services PR #154).
 * When false: legacy CircularProgress + percent score.
 * When true: LetterGradeCard + DimensionBars + LegitimacyBadge + RedFlagsPill,
 * with per-row fallback to legacy if a row's letter_grade is null.
 */
export const enableLetterGrades = readBoolFlag('VITE_ENABLE_LETTER_GRADES')

/**
 * Optional public severance claim and HR administration surfaces. The authenticated
 * /benefits/election route remains registered independently so a participant already
 * placed behind the server-side severance gate can never be stranded by this flag.
 * Mirrors the backend BENEFITS_ELECTION_ENABLED gate for optional entry points.
 */
export const enableBenefits = readBoolFlag('VITE_ENABLE_BENEFITS')

/**
 * Guided transition-support hub and topic routes. Enabled by default while the
 * prototype is evaluated; set the flag to false for an immediate rollback.
 */
export const enableSupport = readBoolFlag('VITE_ENABLE_SUPPORT', true)

/**
 * Managed human-coaching and scheduling experience.
 * Enabled by default; can be disabled independently from Support.
 */
export const enableCoaching = readBoolFlag('VITE_ENABLE_COACHING', true)

/**
 * Animated role and skill-path exploration experience.
 */
export const enableSkillsMap = readBoolFlag('VITE_ENABLE_SKILLS_MAP', true)

/**
 * Self-service coach roster (business-start, retirement): browse and book
 * any coach on the roster with no managed-match assignment. Nested under
 * Coaching; independently switchable off it.
 */
export const enableCoachRoster = readBoolFlag('VITE_ENABLE_COACH_ROSTER', true)

/**
 * Personalized learning module and routes. Disabled by default while Learn is
 * removed from the primary job-seeker experience; enable it explicitly for
 * environments that still need access.
 */
export const enableLearn = readBoolFlag('VITE_ENABLE_LEARN')

/**
 * "Reset demo state" button for the Delgo demo tenant (severance-admin
 * portal). Defaults to false -- only ever set true in a non-production env
 * file. The backend endpoint has its own independent production refusal, so
 * this flag is a UI-layer gate, not the only one.
 */
export const enableDemoReset = readBoolFlag('VITE_ENABLE_DEMO_RESET')

/**
 * Cost & Savings (ROI simulator) under Severance. Defaults to false --
 * hidden everywhere until the underlying calculation is verified (PEL-910).
 * Flip to true, not remove, once that's confirmed.
 */
export const enableCostSavings = readBoolFlag('VITE_ENABLE_COST_SAVINGS')

/**
 * AI Interview practice (/interview). When false, the routes are not
 * registered and the nav item is hidden. Mirrors the backend
 * AI_INTERVIEW_ENABLED gate (services/app/core/config.py) -- both default
 * to true since the feature is already live; this only matters for an
 * explicit rollback.
 */
export const enableAiInterview = readBoolFlag('VITE_ENABLE_AI_INTERVIEW', true)

/**
 * Internal-only tone-threshold testing UI on the AI Interview setup/results
 * pages (default vs tweaked threshold set, raw metric debug panel). Defaults
 * to false so members never see it; only ever set true in a non-production
 * env file for testers comparing threshold sets.
 */
export const isInternalTestingEnabled = readBoolFlag('VITE_IS_INTERNAL_TESTING_ENABLED')
